import { keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type {
  EquityRow,
  ETFRow,
  MutualFundRow,
  SearchResponse,
  TopResultIdentifier,
} from '#/api/generated/fINRAMarketDataAPI.schemas';
import { useGetSearchResponse } from '#/api/generated/search/search';
import { isSuccessResponse } from '#/lib/api-client';

import type { SearchPreviewState } from './searchPagination';
import { buildSearchState } from './searchPagination';

const SEARCH_PREVIEW_DEBOUNCE_MS = 200;
export const SEARCH_PREVIEW_MIN_QUERY_LENGTH = 2;
const SEARCH_PREVIEW_LIMIT = 2;

const SEARCH_PREVIEW_LABELS = {
  equity: 'Equities',
  etf: 'ETFs',
  mutual_fund: 'Mutual Funds',
} as const;

export type SearchPreviewInvestmentType = keyof typeof SEARCH_PREVIEW_LABELS;

type SearchPreviewRow = Pick<
  EquityRow | ETFRow | MutualFundRow,
  'ticker' | 'name' | 'price'
>;

export interface SearchPreviewGroup {
  investmentType: SearchPreviewInvestmentType;
  label: string;
  results: SearchPreviewRow[];
}

export type SearchPreviewTopResult = Pick<
  TopResultIdentifier,
  'investment_type' | 'ticker' | 'name'
>;

export function useSearchPreview(
  query: string,
  searchState?: SearchPreviewState,
  enabled = true,
) {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [lastSettledResultfulResponse, setLastSettledResultfulResponse] =
    useState<SearchResponse | undefined>();
  const [resultfulResponseCache, setResultfulResponseCache] = useState<
    Record<string, SearchResponse>
  >({});

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, SEARCH_PREVIEW_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [query]);

  const normalizedQuery = query.trim();
  const isEligible = normalizedQuery.length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH;

  const previewQuery = useGetSearchResponse(
    buildSearchState({
      query: debouncedQuery,
      investment_type: searchState?.investment_type,
      sort_by: searchState?.sort_by,
      order_by: searchState?.order_by,
      equity_sector_slug: searchState?.equity_sector_slug,
      fund_category_slug: searchState?.fund_category_slug,
      listing_type: searchState?.listing_type,
      exchange: searchState?.exchange,
    }),
    {
      query: {
        enabled:
          enabled && debouncedQuery.length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH,
        placeholderData: keepPreviousData,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  const response = isSuccessResponse(previewQuery.data)
    ? previewQuery.data.data
    : undefined;
  const hasFetchedPreview = previewQuery.isFetched && !previewQuery.isError;
  const hasRenderablePreview = response != null && !previewQuery.isError;
  const searchStateKey = JSON.stringify(searchState ?? {});
  const cacheKey = `${searchStateKey}:${normalizedQuery}`;
  const debouncedCacheKey = `${searchStateKey}:${debouncedQuery}`;
  const cachedResultfulResponse = resultfulResponseCache[cacheKey];
  const isAwaitingDebouncedQuery =
    isEligible &&
    debouncedQuery !== normalizedQuery &&
    (hasRenderablePreview || cachedResultfulResponse != null);
  const isRefreshingPreview =
    (hasRenderablePreview || cachedResultfulResponse != null) &&
    (isAwaitingDebouncedQuery || previewQuery.isFetching);
  const displayResponse = isRefreshingPreview
    ? (cachedResultfulResponse ?? lastSettledResultfulResponse ?? response)
    : response;
  const groups = getSearchPreviewGroups(
    displayResponse,
    displayResponse?.top_result_identifier,
  );
  const hasPreviewResults =
    Boolean(displayResponse?.top_result_identifier) ||
    groups.some((group) => group.results.length > 0);
  const shouldRenderPreview = enabled && isEligible && displayResponse != null;
  const showLoadingOnlyState =
    shouldRenderPreview && isRefreshingPreview && !hasPreviewResults;
  const showEmptyState =
    shouldRenderPreview && !isRefreshingPreview && !hasPreviewResults;

  useEffect(() => {
    if (
      !isEligible ||
      previewQuery.isError ||
      response == null ||
      isRefreshingPreview
    ) {
      return;
    }

    if (
      !hasSearchPreviewResults(response) ||
      debouncedQuery.length < SEARCH_PREVIEW_MIN_QUERY_LENGTH
    ) {
      setLastSettledResultfulResponse(undefined);
      return;
    }

    setLastSettledResultfulResponse(response);

    setResultfulResponseCache((currentCache) => {
      if (currentCache[debouncedCacheKey] === response) {
        return currentCache;
      }

      return {
        ...currentCache,
        [debouncedCacheKey]: response,
      };
    });
  }, [
    debouncedCacheKey,
    debouncedQuery,
    searchStateKey,
    isEligible,
    isRefreshingPreview,
    previewQuery.isError,
    response,
  ]);

  return {
    normalizedQuery,
    response,
    topResult: displayResponse?.top_result_identifier,
    groups,
    hasFetchedPreview,
    hasPreviewResults,
    isRefreshingPreview,
    shouldRenderPreview,
    showLoadingOnlyState,
    showEmptyState,
  };
}

function hasSearchPreviewResults(response: SearchResponse | undefined) {
  if (!response) {
    return false;
  }

  return (
    Boolean(response.top_result_identifier) ||
    Boolean(response.equity?.results?.length) ||
    Boolean(response.etf?.results?.length) ||
    Boolean(response.mutual_fund?.results?.length)
  );
}

function getSearchPreviewGroups(
  response: SearchResponse | undefined,
  topResult?: SearchResponse['top_result_identifier'],
): SearchPreviewGroup[] {
  const matchesTopResult = (investmentType: SearchPreviewInvestmentType) => {
    return (result: SearchPreviewRow) => {
      if (!topResult || topResult.investment_type !== investmentType) {
        return false;
      }

      if (topResult.ticker && result.ticker) {
        return topResult.ticker === result.ticker;
      }

      if (topResult.name && result.name) {
        return topResult.name === result.name;
      }

      return false;
    };
  };

  return [
    {
      investmentType: 'equity',
      label: SEARCH_PREVIEW_LABELS.equity,
      results: (response?.equity?.results ?? [])
        .filter((result) => !matchesTopResult('equity')(result))
        .slice(0, SEARCH_PREVIEW_LIMIT),
    },
    {
      investmentType: 'etf',
      label: SEARCH_PREVIEW_LABELS.etf,
      results: (response?.etf?.results ?? [])
        .filter((result) => !matchesTopResult('etf')(result))
        .slice(0, SEARCH_PREVIEW_LIMIT),
    },
    {
      investmentType: 'mutual_fund',
      label: SEARCH_PREVIEW_LABELS.mutual_fund,
      results: (response?.mutual_fund?.results ?? [])
        .filter((result) => !matchesTopResult('mutual_fund')(result))
        .slice(0, SEARCH_PREVIEW_LIMIT),
    },
  ];
}
